'use client';

import { useState, useContext, type FC, useEffect } from 'react';
import {
  Button,
  ButtonGroup,
  Flex,
  Input,
  Dialog,
  TrixEditor,
} from '@/_components';
import { editMessage, editScheduledMessage } from '@/_api/admin-conversations';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import {
  getDateFromInputs,
  getDateAndTimeFromString,
  getNDaysAfter,
  getIsPastDate,
} from '@/_utils/dates';
import type { MessageDialogProps } from './MessageDialog.types';

const TODAYS_DATE: string = getNDaysAfter(0);

export const MessageDialog: FC<MessageDialogProps> = ({
  message,
  isEditMessageOpen,
  isEditScheduleTimeOpen,
  onCloseEditMessage,
  onCloseEditScheduleTime,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { reloadMessages } = useContext(AdminConversationsContext);

  const [isEditMessageLoading, setEditMessageLoading] =
    useState<boolean>(false);
  const [isEditScheduleTimeLoading, setEditScheduleTimeLoading] =
    useState<boolean>(false);
  const [messageText, setMessageText] = useState<string>('');
  const [scheduleDate, setScheduleDate] = useState<string>('');
  const [scheduleTime, setScheduleTime] = useState<string>('');

  useEffect(() => {
    if (message && isEditMessageOpen) {
      setMessageText(message.text || '');
    }
  }, [message, isEditMessageOpen]);

  useEffect(() => {
    if (message?.scheduled_time && isEditScheduleTimeOpen) {
      const { date, time } = getDateAndTimeFromString(message.scheduled_time);
      setScheduleDate(date);
      setScheduleTime(time);
    }
  }, [message, isEditScheduleTimeOpen]);

  const onScheduleDateChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setScheduleDate(event.target.value);
  const onScheduleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setScheduleTime(event.target.value);

  const isUpdatedScheduleDateValid: boolean = !getIsPastDate(scheduleDate);

  const onConfirmEdit = async () => {
    try {
      setEditMessageLoading(true);
      if (!messageText) {
        return addToast('error', 'Please enter a message');
      }

      await editMessage({
        message_id: message?.id!,
        text: messageText,
        token,
      });

      addToast('success', 'Message updated successfully');
    } catch (error) {
      console.error(error);
      addToast('error', 'Failed to update message');
    } finally {
      setEditMessageLoading(false);
      onCloseEditMessage();
      reloadMessages(true);
    }
  };

  const onConfirmEditScheduleTime = async () => {
    try {
      setEditScheduleTimeLoading(true);
      const formatted = getDateFromInputs(scheduleDate, scheduleTime);

      if (!isUpdatedScheduleDateValid) {
        return addToast('error', 'Please select a date in the future');
      }

      await editScheduledMessage({
        scheduled_time: formatted,
        message_id: message?.id!,
        token,
      });

      addToast('success', 'Schedule time updated successfully');
    } catch (error) {
      console.error(error);
      addToast('error', 'Failed to update schedule time');
    } finally {
      setEditScheduleTimeLoading(false);
      onCloseEditScheduleTime();
      reloadMessages(true);
    }
  };

  return (
    <>
      <Dialog
        isOpen={isEditMessageOpen}
        title="Edit Message"
        onClose={onCloseEditMessage}
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            <TrixEditor
              value={messageText}
              setValue={setMessageText}
              isDisabled={isEditMessageLoading}
            />
            <ButtonGroup align="right">
              <Button
                intent="primary"
                value="Update"
                onClick={onConfirmEdit}
                isLoading={isEditMessageLoading}
                isDisabled={isEditMessageLoading}
              />
              <Button
                intent="secondary"
                value="Cancel"
                isDisabled={isEditMessageLoading}
                onClick={onCloseEditMessage}
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>

      <Dialog
        isOpen={isEditScheduleTimeOpen}
        title="Edit Scheduled Time"
        onClose={onCloseEditScheduleTime}
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            <Input
              label="Date"
              min={TODAYS_DATE}
              type="date"
              value={scheduleDate}
              onChange={onScheduleDateChange}
            />
            <Input
              label="Time"
              type="time"
              value={scheduleTime}
              onChange={onScheduleTimeChange}
            />
            <ButtonGroup align="right">
              <Button
                intent="primary"
                value="Update Scheduled Time"
                onClick={onConfirmEditScheduleTime}
                isLoading={isEditScheduleTimeLoading}
                isDisabled={
                  isEditScheduleTimeLoading || !isUpdatedScheduleDateValid
                }
              />
              <Button
                intent="secondary"
                value="Cancel"
                isDisabled={isEditScheduleTimeLoading}
                onClick={onCloseEditScheduleTime}
              />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
